/*
 * "Before you go" — a quiet note for someone about to leave a full cart.
 *
 * When it shows:
 *   - only on the cart page, or while the cart drawer is open;
 *   - only if the cart actually has something in it (checked with /cart.js at
 *     the moment it would show, so a cart emptied in the drawer never triggers);
 *   - at most once per visit (sessionStorage), and never once the shopper has
 *     pressed a checkout button.
 *
 * What counts as "leaving":
 *   - desktop: the pointer leaves through the top of the window (towards the
 *     tabs, address bar or back button), after they've been here a few seconds;
 *   - touch screens have no pointer to watch, so: the cart page has sat idle
 *     for a while, or they flick quickly back up towards the header.
 *
 * No back-button hijacking and no fake timers: it should feel like a shop
 * assistant mentioning something, not a door being held shut.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-mz-cart-exit]');
  if (!root) return;

  var SEEN_KEY = 'mz_cart_exit_seen';
  var MIN_DWELL_MS = 4000;
  var TOUCH_IDLE_MS = 35000;

  var onCartPage = root.getAttribute('data-on-cart-page') === 'true';
  var freeShippingAt = parseFloat(root.getAttribute('data-free-shipping-at')) || 0;
  var offerUntil = root.getAttribute('data-offer-until') || '';
  var card = root.querySelector('.mz-exit__card');
  var startedAt = Date.now();
  var busy = false;
  var lastFocus = null;

  function storage(fn) {
    try {
      return fn(window.sessionStorage);
    } catch (e) {
      return null;
    }
  }

  function alreadySeen() {
    return storage(function (s) { return s.getItem(SEEN_KEY); }) === '1';
  }

  function markSeen() {
    storage(function (s) { s.setItem(SEEN_KEY, '1'); });
  }

  function drawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    return !!(drawer && drawer.classList.contains('active'));
  }

  function inCartContext() {
    return onCartPage || drawerOpen();
  }

  // Someone who pressed checkout is not leaving.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (target && target.closest && target.closest('[name="checkout"]')) markSeen();
    },
    true
  );

  function money(cents, currency) {
    var value = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'CAD',
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  function thumbUrl(src) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
  }

  function fillFromCart(cart) {
    // Up to three pieces, then "+n" for the rest.
    var list = root.querySelector('[data-mz-exit-items]');
    list.innerHTML = '';
    var shown = 0;
    for (var i = 0; i < cart.items.length && shown < 3; i++) {
      var item = cart.items[i];
      if (!item.image) continue;
      var li = document.createElement('li');
      var img = document.createElement('img');
      img.src = thumbUrl(item.image);
      img.alt = item.product_title || '';
      img.loading = 'lazy';
      li.appendChild(img);
      list.appendChild(li);
      shown++;
    }
    var rest = cart.item_count - shown;
    if (shown > 0 && rest > 0) {
      var more = document.createElement('li');
      var box = document.createElement('span');
      box.className = 'mz-exit__more';
      box.textContent = '+' + rest;
      more.appendChild(box);
      list.appendChild(more);
    }

    // What the thank-you offer is already saving them — a fact, not a push.
    var saving = root.querySelector('[data-mz-exit-saving]');
    var saved = cart.total_discount || 0;
    if (saved > 0) {
      saving.innerHTML = '';
      saving.appendChild(document.createTextNode('Our thank-you is taking '));
      var strong = document.createElement('strong');
      strong.textContent = money(saved, cart.currency);
      saving.appendChild(strong);
      saving.appendChild(
        document.createTextNode(' off your order' + (offerUntil ? ', until ' + offerUntil + '.' : '.'))
      );
      saving.hidden = false;
    } else {
      saving.hidden = true;
    }

    // Free shipping: say it's theirs, or how close they are.
    var ship = root.querySelector('[data-mz-exit-shipping]');
    if (ship && freeShippingAt > 0) {
      var short = freeShippingAt * 100 - cart.total_price;
      ship.textContent =
        short <= 0
          ? 'Your order ships free'
          : money(short, cart.currency) + ' away from free shipping';
    }
  }

  function open() {
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (card) card.focus();
  }

  function close() {
    root.hidden = true;
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function maybeShow() {
    if (busy || !root.hidden || alreadySeen() || !inCartContext()) return;
    if (Date.now() - startedAt < MIN_DWELL_MS) return;
    busy = true;

    fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count || alreadySeen() || !inCartContext()) return;
        fillFromCart(cart);
        markSeen();
        open();
      })
      .catch(function () {})
      .then(function () { busy = false; });
  }

  // Close: scrim, ×, "keep browsing", Escape.
  root.addEventListener('click', function (event) {
    if (event.target.closest('[data-mz-exit-close]')) close();
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && !root.hidden) close();
  });

  // "Complete my order" goes through the page's own checkout button where there
  // is one, so the store's checkout flow (Global-e included) runs exactly as it
  // would from the cart. Otherwise it takes them to the cart.
  root.querySelector('[data-mz-exit-checkout]').addEventListener('click', function (event) {
    var buttons = document.querySelectorAll('[name="checkout"]');
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].offsetParent !== null && !buttons[i].disabled) {
        event.preventDefault();
        close();
        buttons[i].click();
        return;
      }
    }
  });

  // Desktop: leaving through the top edge.
  document.addEventListener('mouseout', function (event) {
    if (event.relatedTarget || event.toElement) return;
    if (event.clientY > 12) return;
    maybeShow();
  });

  // Touch: idle on the cart page, or a fast flick back up.
  var touch = window.matchMedia && window.matchMedia('(hover: none)').matches;
  if (touch) {
    var idleTimer = null;
    var resetIdle = function () {
      if (!onCartPage) return;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(maybeShow, TOUCH_IDLE_MS);
    };
    ['touchstart', 'scroll'].forEach(function (type) {
      window.addEventListener(type, resetIdle, { passive: true });
    });
    resetIdle();

    var lastY = window.scrollY;
    var lastT = Date.now();
    window.addEventListener(
      'scroll',
      function () {
        var y = window.scrollY;
        var t = Date.now();
        var speed = (lastY - y) / Math.max(t - lastT, 1); // px per ms, upwards
        if (speed > 2.5 && y < 400) maybeShow();
        lastY = y;
        lastT = t;
      },
      { passive: true }
    );
  }
})();
